var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Autocomplete, Box, Button, FormControl, FormHelperText, Grid, IconButton, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowLeft02Icon } from 'hugeicons-react';
import { enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { RoleCategories } from '../../../../application/common/constants/role-categories';
import { useGetAllCompaniesQuery } from '../../../../application/features/companies/company-api';
import { useLazyGetRolePermissionsQuery, useUpdateRolePermissionsMutation } from '../../../../application/features/roles/role-api';
import { useAppSelector } from '../../../../application/store/useStore';
import useTranslate from '../../../../localization/helpers';
import DetailedPermissions from './DetailedPermissions';
import GroupPermissions from './GroupPermissions';
import MobilePermissions from './mobile-permissions';
var RolePermissions = function () {
    var theme = useTheme();
    var currentUser = useAppSelector(function (state) { return state.auth; }).currentUser;
    var _a = useTranslate(), translate = _a.translate, tWithField = _a.tWithField;
    var isXs = useMediaQuery(theme.breakpoints.only('xs'));
    var isSm = useMediaQuery(theme.breakpoints.only('sm'));
    var isscreen600900 = useMediaQuery('(min-width:600px) and (max-width:900px)');
    var iscreen9001200 = useMediaQuery('(min-width:900px) and (max-width:1200px)');
    var isIpadMini = useMediaQuery('(max-width: 768px)');
    var isIpadMiniLandscape = useMediaQuery('(max-width: 1024px) and (orientation: landscape)');
    var isLg = useMediaQuery(theme.breakpoints.up('lg'));
    var navigate = useNavigate();
    var roleId = useParams().roleId;
    var _b = __read(useUpdateRolePermissionsMutation(), 2), updateRolePermissions = _b[0], _c = _b[1], isLoading = _c.isLoading, isSuccess = _c.isSuccess;
    var _d = __read(useLazyGetRolePermissionsQuery(), 2), getRolePermissions = _d[0], rolePermissions = _d[1].data;
    var _e = (rolePermissions !== null && rolePermissions !== void 0 ? rolePermissions : {}).permissionGroups, permissionGroups = _e === void 0 ? [] : _e;
    var _f = useGetAllCompaniesQuery({
        filterByCompany: true
    }), _g = _f.data, companies = _g === void 0 ? [] : _g, isCompaniesFetching = _f.isFetching;
    var _h = useForm({
        defaultValues: {
            id: '',
            name: '',
            category: '',
            companyId: '',
            permissionGroups: []
        }
    }), control = _h.control, handleSubmit = _h.handleSubmit, setValue = _h.setValue, reset = _h.reset, errors = _h.formState.errors;
    useEffect(function () {
        if (roleId)
            getRolePermissions(roleId);
    }, [getRolePermissions, roleId]);
    useEffect(function () {
        if (rolePermissions)
            reset({
                id: rolePermissions.id,
                name: rolePermissions.name,
                category: rolePermissions.category,
                companyId: rolePermissions.companyId,
                permissionGroups: rolePermissions.permissionGroups
            });
    }, [rolePermissions, reset]);
    useEffect(function () {
        if (isSuccess) {
            enqueueSnackbar(tWithField('toolTips.updateSuccess', 'common.rolesPermissions'), {
                autoHideDuration: 3000,
                variant: 'success',
                anchorOrigin: { horizontal: 'right', vertical: 'top' }
            });
            navigate(-1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);
    var navigateBack = function () { return navigate(-1); };
    var onSubmit = function (formValues) {
        var permissions = formValues.permissionGroups.flatMap(function (group) {
            return group.permissions.filter(function (p) { return p.permissionName; });
        });
        var updatePermissionsDto = {
            id: formValues.id,
            name: formValues.name,
            category: formValues.category,
            companyId: formValues.companyId,
            permissions: permissions
        };
        updateRolePermissions(updatePermissionsDto);
    };
    if (!rolePermissions)
        return _jsx(_Fragment, {});
    return (_jsxs(Box, { sx: {
            width: '100%',
            padding: isXs ? '16px' : '8px',
            display: 'flex',
            height: 'max-content',
            flexDirection: 'column',
            position: 'relative'
        }, children: [_jsxs(Box, { padding: isXs ? undefined : isSm ? 1 : 1, display: 'flex', flexDirection: 'row', mb: 1, alignItems: 'center', children: [_jsx(IconButton, { color: 'primary', onClick: navigateBack, children: _jsx(ArrowLeft02Icon, { size: 24 }) }), _jsxs(Typography, { variant: 'h3', color: '#009983', children: [rolePermissions === null || rolePermissions === void 0 ? void 0 : rolePermissions.name, " ", translate('roles.permissions')] })] }), (isXs && isIpadMini && isIpadMiniLandscape) ||
                (!isLg && (_jsx(Box, { display: 'flex', justifyContent: 'flex-end', my: 1, children: _jsx(Button, { variant: 'contained', color: 'warning', size: isXs ? 'small' : 'medium', disabled: isLoading, onClick: handleSubmit(onSubmit), children: translate('actions.saveChanges') }) }))), _jsxs(Grid, { container: true, spacing: { xs: 1, md: 2 }, my: 2, children: [_jsx(Grid, { size: { xs: 12, lg: 4 }, children: _jsx(Controller, { control: control, name: 'name', render: function (_a) {
                                var _b = _a.field, onChange = _b.onChange, value = _b.value;
                                return (_jsx(FormControl, { size: 'small', fullWidth: true, children: _jsx(TextField, { variant: 'outlined', size: 'small', disabled: isLoading ||
                                            ((rolePermissions === null || rolePermissions === void 0 ? void 0 : rolePermissions.isSystem) &&
                                                rolePermissions.category === RoleCategories.Technician), fullWidth: true, label: translate('roles.roleName'), onChange: onChange, value: value }) }));
                            } }) }), _jsx(Grid, { size: { xs: 12, lg: 4 }, children: _jsx(Controller, { name: 'category', control: control, rules: {
                                required: {
                                    value: true,
                                    message: tWithField('validations.fieldIsRequired', 'roles.category')
                                }
                            }, render: function (_a) {
                                var _b;
                                var _c = _a.field, onChange = _c.onChange, value = _c.value;
                                return (_jsxs(FormControl, { size: 'small', fullWidth: true, children: [_jsx(InputLabel, { disabled: isLoading || (rolePermissions === null || rolePermissions === void 0 ? void 0 : rolePermissions.isSystem), children: translate('roles.category') }), _jsxs(Select, { label: translate('roles.category'), value: value, disabled: isLoading || (rolePermissions === null || rolePermissions === void 0 ? void 0 : rolePermissions.isSystem), onChange: onChange, error: !!errors.category, children: [_jsx(MenuItem, { value: 'Others', children: translate('roles.others') }), _jsx(MenuItem, { value: 'Technician', children: translate('common.technician') })] }), _jsx(FormHelperText, { error: true, children: (_b = errors.category) === null || _b === void 0 ? void 0 : _b.message })] }));
                            } }) }), (currentUser === null || currentUser === void 0 ? void 0 : currentUser.roleCategory) === RoleCategories.SuperAdmin && (_jsx(Grid, { size: { xs: 12, lg: 4 }, children: _jsx(Controller, { control: control, name: 'companyId', rules: {
                                required: {
                                    value: true,
                                    message: tWithField('validations.fieldIsRequired', 'common.company')
                                }
                            }, render: function (_a) {
                                var _b, _c;
                                var _d = _a.field, value = _d.value, onChange = _d.onChange;
                                return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(Autocomplete, { noOptionsText: translate('common.noOptions'), size: 'small', sx: {
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '8px',
                                                    '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                                                        padding: '3.5px 4px 3.5px 8px'
                                                    }
                                                }
                                            }, options: companies.filter(function (company) { return company.isActive === true; }), value: (_b = companies.find(function (c) { return c.id === value; })) !== null && _b !== void 0 ? _b : null, loading: isCompaniesFetching, disabled: true, isOptionEqualToValue: function (option, value) { return option.id === value.id; }, getOptionLabel: function (option) { return option.name; }, renderInput: function (params) { return (_jsx(TextField, __assign({ error: !!errors.companyId }, params, { variant: 'outlined', fullWidth: true, label: translate('common.company') }))); }, onChange: function (_, value) {
                                                onChange(value === null || value === void 0 ? void 0 : value.id);
                                            } }), _jsx(FormHelperText, { error: true, children: (_c = errors.companyId) === null || _c === void 0 ? void 0 : _c.message })] }));
                            } }) }))] }), isXs || isIpadMini || isIpadMiniLandscape ? (_jsx(MobilePermissions, { permissionGroups: permissionGroups, control: control, translate: translate, setValue: setValue })) : (_jsx(_Fragment, { children: _jsx(TableContainer, { sx: {
                        maxHeight: __assign(__assign({ sm: 'calc(100svh - 345px)' }, (isscreen600900 && { sm: '600px' })), (iscreen9001200 && { sm: '600px' })),
                        boxShadow: '0 24px 48px 0 #919EAB29',
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        overflowX: 'auto',
                        overflowY: 'auto'
                    }, children: _jsxs(Table, { stickyHeader: true, sx: {
                            p: 2
                        }, "aria-label": 'detailed permissions table', children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: translate('roles.modules') }), _jsx(TableCell, { align: 'center', children: translate('roles.read') }), _jsx(TableCell, { align: 'center', children: translate('roles.update') }), _jsx(TableCell, { align: 'center', children: translate('roles.create') }), _jsx(TableCell, { align: 'center', children: translate('roles.delete') })] }) }), _jsx(TableBody, { children: permissionGroups.map(function (group, groupIndex) { return (_jsxs(TableRow, { children: [_jsx(TableCell, { component: 'th', scope: 'row', children: translate("common.".concat(group.groupName.toLowerCase())) }), _jsx(GroupPermissions, { control: control, groupIndex: groupIndex, setValue: setValue })] }, groupIndex)); }) })] }) }) })), (!isXs || !isIpadMini || !isIpadMiniLandscape) && isLg && (_jsx(Box, { marginTop: 3, display: 'flex', justifyContent: 'flex-end', children: _jsx(Button, { variant: 'contained', color: 'warning', size: 'medium', disabled: isLoading, onClick: handleSubmit(onSubmit), children: translate('actions.saveChanges') }) })), _jsx(DetailedPermissions, { isOpen: false, onClose: function () { } })] }));
};
export default RolePermissions;
